import React, { useState, useMemo } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Settings,
  Copy,
  Plus,
  X,
  Zap,
  RotateCcw,
} from 'lucide-react';
import { Input } from './@/components/ui/input';
import { Switch } from './@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './@/components/ui/select';
import { cn } from './@/lib/utils';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import OpenShortShiftsSection from './OpenShortShiftsSection';
import './ConfigPanel.css';

// ── Shared constants ─────────────────────────────────────────────
// Full 24h in 30-min increments so morning/late-night shifts always have a label.
const SHIFT_TIME_OPTIONS = [
  'open',
  'close',
  ...Array.from({ length: 48 }, (_, i) => {
    const totalMin = i * 30;
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }),
];

// Canonical operational areas (RoleConfiguration.operationalArea value space).
// Fixed list so SECTIONS keys can't drift from what the engine resolves.
const SECTION_AREAS = ['FRONT_OF_HOUSE', 'BACK_OF_HOUSE', 'BAR'];

const DAY_KEYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
const DAY_SHORT = { MONDAY: 'Mon', TUESDAY: 'Tue', WEDNESDAY: 'Wed', THURSDAY: 'Thu', FRIDAY: 'Fri', SATURDAY: 'Sat', SUNDAY: 'Sun' };

// Parameters grouped by intent for rule cards
const PRIMARY_NUMERIC_KEYS = new Set([
  'MIN', 'MAX', 'IDEAL', 'BETWEEN_SHIFTS',
  'MAX_INTERNATIONAL_HOURS', 'MAX_YOUTH_WORKER_HOURS',
  'ROLE_MIN', 'ROLE_MAX', 'EVENT_TRAVEL_BUFFER_MINUTES', 'MAX_BUFFER_MINUTES',
  'MAX_BREAKS_AT_A_TIME',
  'MIN_TIME_AFTER_BEGINNING_OF_SHIFT_TO_ALLOW_BREAK',
  'MIN_TIME_BEFORE_END_OF_SHIFT_TO_ALLOW_BREAK',
  'AVOID_BREAK_DURING_LAST_X_PERCENT_OF_SHIFT',
  'REDUCE_EMPLOYEES_WITH_SKILL_LEVEL_LOWER_THAN_X_DURING_BUSY_PERIODS',
  'MIN_HEADCOUNT_BEFORE_PLACEMENT',
  'MAX_YOUTH_WORKER_DAILY_HOURS',
  'YOUTH_BETWEEN_SHIFTS', 'YOUTH_REST_PER_WEEK',
]);

const TIME_STRING_KEYS = new Set(['YOUTH_WORKER_MAX_END', 'YOUTH_WORKER_MIN_START']);

// Convert snake/upper case to Title Case words
const humanize = (key) =>
  key
    .toString()
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');

// Convert a shift endpoint to a display string
const formatShiftValue = (v) => {
  if (v === 'open' || v === 'close') return v;
  if (Array.isArray(v)) return `${String(v[0]).padStart(2, '0')}:${String(v[1]).padStart(2, '0')}`;
  return String(v);
};

const parseShiftValue = (str) => {
  if (str === 'open' || str === 'close') return str;
  const [h, m] = str.split(':').map(Number);
  return [h, m];
};

const ConfigPanel = ({ config, setConfig, isLight, area, setOrderedDays, roleOrder, setRoleOrder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeRole, setActiveRole] = useState(null);

  const roles = useMemo(() => Object.keys(config.ROLES || {}), [config.ROLES]);
  const currentRole = activeRole && roles.includes(activeRole) ? activeRole : roles[0];

  // ── State mutation helpers ─────────────────────────────────────
  const handleChange = (path, value) => {
    setConfig((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        if (cur[keys[i]] == null || typeof cur[keys[i]] !== 'object') cur[keys[i]] = {};
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const { source, destination } = result;
    if (source.droppableId === 'days') {
      const items = Array.from(config.ORDERED_DAYS);
      const [moved] = items.splice(source.index, 1);
      items.splice(destination.index, 0, moved);
      handleChange('ORDERED_DAYS', items);
      setOrderedDays(items);
    } else if (source.droppableId === 'roles') {
      const items = Array.from(roleOrder);
      const [moved] = items.splice(source.index, 1);
      items.splice(destination.index, 0, moved);
      setRoleOrder(items);
    }
  };

  // ── Early/Late cutoff selects ──────────────────────────────────
  const CUTOFF_TIME_OPTIONS = SHIFT_TIME_OPTIONS.filter((o) => o !== 'open' && o !== 'close');

  const renderCutoffSelect = (value, onChange) => {
    const display = typeof value === 'string' ? value : '';
    const options = display && !CUTOFF_TIME_OPTIONS.includes(display)
      ? [display, ...CUTOFF_TIME_OPTIONS]
      : CUTOFF_TIME_OPTIONS;
    return (
      <Select value={display} onValueChange={onChange}>
        <SelectTrigger className="cfg-time-trigger">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="cfg-time-content">
          {options.map((opt) => (
            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  const renderCutoffs = () => (
    <div className="cfg-metabar-group">
      <span className="cfg-metabar-label">Early / Late cutoff</span>
      <div className="cfg-cutoff-row">
        <div className="cfg-cutoff-field">
          <span className="cfg-cutoff-sublabel">early</span>
          {renderCutoffSelect(
            config.EARLY_CUTOFF,
            (v) => handleChange('EARLY_CUTOFF', v)
          )}
        </div>
        <div className="cfg-cutoff-field">
          <span className="cfg-cutoff-sublabel">late</span>
          {renderCutoffSelect(
            config.LATE_CUTOFF,
            (v) => handleChange('LATE_CUTOFF', v)
          )}
        </div>
      </div>
    </div>
  );

  // ── Top meta row (days, roles, rerun) ──────────────────────────
  const renderTopRow = () => (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="cfg-metabar">
        <div className="cfg-metabar-group">
          <span className="cfg-metabar-label">Week order</span>
          <Droppable droppableId="days" direction="horizontal">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="cfg-chip-row">
                {config.ORDERED_DAYS.map((day, idx) => (
                  <Draggable key={day} draggableId={`day-${day}`} index={idx}>
                    {(prov, snap) => (
                      <div
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        {...prov.dragHandleProps}
                        className={cn('cfg-drag-chip', snap.isDragging && 'is-dragging')}
                      >
                        {day.slice(0, 3)}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>

        {roleOrder && roleOrder.length > 0 && (
          <div className="cfg-metabar-group">
            <span className="cfg-metabar-label">Role order</span>
            <Droppable droppableId="roles" direction="horizontal">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} className="cfg-chip-row">
                  {roleOrder.map((role, idx) => (
                    <Draggable key={role} draggableId={`role-${role}`} index={idx}>
                      {(prov, snap) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          {...prov.dragHandleProps}
                          className={cn('cfg-drag-chip', snap.isDragging && 'is-dragging')}
                        >
                          {role}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        )}

        {renderCutoffs()}

        <div className="cfg-rerun">
          <span className="cfg-metabar-label">Rerun</span>
          <Switch
            checked={config.RERUN}
            onCheckedChange={(c) => handleChange('RERUN', c)}
          />
        </div>
      </div>
    </DragDropContext>
  );

  // ── Constraints strip ──────────────────────────────────────────
  const renderConstraints = () => (
    <section className="cfg-section">
      <header className="cfg-section-head">
        <span className="cfg-section-kicker">01</span>
        <h3 className="cfg-section-title">Constraints</h3>
        <span className="cfg-section-sub">global on/off for each rule family</span>
      </header>
      <div className="cfg-constraints">
        {Object.entries(config.CONSTRAINTS)
          .filter(([, enabled]) => typeof enabled === 'boolean')
          .map(([name, enabled]) => (
          <label key={name} className={cn('cfg-constraint', enabled && 'is-on')}>
            <Switch
              checked={enabled}
              onCheckedChange={(c) => handleChange(`CONSTRAINTS.${name}`, c)}
            />
            <span>{humanize(name)}</span>
          </label>
        ))}
      </div>
      <div className="cfg-shifts">
        <label className="cfg-field" title="Weights salaried (FIXED) ideal-shift rewards above the contracted hourly pool in the shared LP objective (1.0 = no-op).">
          <span className="cfg-field-label">Salaried ideal priority multiplier</span>
          <Input
            type="number"
            step="any"
            min={0}
            className="cfg-num-input"
            value={config.CONSTRAINTS.SALARIED_IDEAL_PRIORITY_MULTIPLIER ?? 1.0}
            onChange={(e) => {
              const v = e.target.value;
              handleChange('CONSTRAINTS.SALARIED_IDEAL_PRIORITY_MULTIPLIER', v === '' ? 1.0 : parseFloat(v));
            }}
          />
        </label>
      </div>
    </section>
  );

  // ── Key holders (OPEN_CLOSE_RULE) ──────────────────────────────
  const renderKeyHolders = () => {
    const oc = config.OPEN_CLOSE_RULE;
    if (!oc) return null;
    const holders = oc.KEY_HOLDERS || [];
    const disabled = !oc.ACTIVE;
    const update = (next) => handleChange('OPEN_CLOSE_RULE.KEY_HOLDERS', next);

    return (
      <section className="cfg-section">
        <header className="cfg-section-head">
          <span className="cfg-section-kicker">02</span>
          <h3 className="cfg-section-title">Key holders</h3>
          <span className="cfg-section-sub">job titles eligible to open or close the site</span>
          <div className="cfg-role-switch" style={{ marginLeft: 'auto' }}>
            <Switch
              checked={!!oc.ACTIVE}
              onCheckedChange={(c) => handleChange('OPEN_CLOSE_RULE.ACTIVE', c)}
            />
            <span>{oc.ACTIVE ? 'open/close rule on' : 'open/close rule off'}</span>
          </div>
        </header>
        <div className="cfg-shifts">
          {holders.length === 0 && (
            <span className="cfg-shifts-empty">No key holders configured</span>
          )}
          {holders.map((title, idx) => (
            <div key={idx} className="cfg-shift-chip">
              <Input
                type="text"
                className="cfg-text-input"
                disabled={disabled}
                value={title}
                onChange={(e) => {
                  const next = [...holders];
                  next[idx] = e.target.value;
                  update(next);
                }}
              />
              <button
                className="cfg-shift-remove"
                disabled={disabled}
                onClick={() => update(holders.filter((_, i) => i !== idx))}
                aria-label="Remove key holder"
              >
                <X size={12} strokeWidth={2.5} />
              </button>
            </div>
          ))}
          <button
            className="cfg-shift-add"
            disabled={disabled}
            onClick={() => update([...holders, ''])}
          >
            <Plus size={12} strokeWidth={2.5} /> key holder
          </button>
        </div>

        {renderOpenCloseSubRules(oc, disabled)}
      </section>
    );
  };

  // ── Intra-week open/close fairness sub-rules ───────────────────
  // Two additive rules layered on top of the key-holder gate; both gated
  // by OPEN_CLOSE_RULE.ACTIVE above (disabled=true when off).
  const renderOpenCloseSubRules = (oc, parentDisabled) => {
    const rotationActive = !!oc.ROTATION_ACTIVE;
    const rotationWeight = oc.ROTATION_WEIGHT ?? 5.0;
    const ctoActive = !!oc.CLOSE_THEN_OPEN_ACTIVE;
    const ctoPenalty = oc.CLOSE_THEN_OPEN_PENALTY ?? 2000.0;
    const sharedAfter = oc.MINUTES_AFTER_OPENING ?? 30;
    const sharedBefore = oc.MINUTES_BEFORE_CLOSING ?? 30;

    const subRule = ({ title, sub, activeKey, active, weightKey, weight, weightLabel, windows = [] }) => {
      const rowDisabled = parentDisabled || !active;
      return (
        <article className={cn('cfg-rule', !active && 'is-inactive')}>
          <header className="cfg-rule-head">
            <div className="cfg-rule-title-block">
              <Switch
                checked={active}
                disabled={parentDisabled}
                onCheckedChange={(c) => handleChange(`OPEN_CLOSE_RULE.${activeKey}`, c)}
              />
              <h4 className="cfg-rule-title">{title}</h4>
            </div>
            <div className="cfg-rule-meta">
              <label className="cfg-priority" title={weightLabel}>
                <Zap size={11} strokeWidth={2.5} />
                <Input
                  type="number"
                  min="0"
                  step="any"
                  className="cfg-priority-input"
                  disabled={rowDisabled}
                  value={weight}
                  onChange={(e) => {
                    const v = e.target.value;
                    handleChange(
                      `OPEN_CLOSE_RULE.${weightKey}`,
                      v === '' ? 0 : parseFloat(v),
                    );
                  }}
                />
              </label>
            </div>
          </header>
          <div className="cfg-rule-body">
            <span className="cfg-field-label">{sub}</span>
            {windows.length > 0 && (
              <div className="cfg-rule-fields" style={{ marginTop: 8 }}>
                {windows.map(({ label, key, value, fallback, title: hint }) => (
                  <label key={key} className="cfg-field" title={hint}>
                    <span className="cfg-field-label">{label}</span>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      className="cfg-num-input"
                      disabled={rowDisabled}
                      placeholder={`${fallback}`}
                      value={value ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        handleChange(`OPEN_CLOSE_RULE.${key}`, v === '' ? null : parseInt(v, 10));
                      }}
                    />
                  </label>
                ))}
              </div>
            )}
          </div>
        </article>
      );
    };

    return (
      <div className="cfg-rules-grid" style={{ marginTop: 12 }}>
        {subRule({
          title: 'Rotation tracking',
          sub: 'Penalise repeat opens/closes within the week.',
          activeKey: 'ROTATION_ACTIVE',
          active: rotationActive,
          weightKey: 'ROTATION_WEIGHT',
          weight: rotationWeight,
          weightLabel: 'Weight per prior open/close already assigned this week',
          windows: [
            {
              label: 'after open (min)',
              key: 'ROTATION_MINUTES_AFTER_OPENING',
              value: oc.ROTATION_MINUTES_AFTER_OPENING,
              fallback: sharedAfter,
              title: 'Window after site opens within which a shift counts as a prior open for rotation tracking. Leave empty to fall back to MINUTES_AFTER_OPENING.',
            },
            {
              label: 'before close (min)',
              key: 'ROTATION_MINUTES_BEFORE_CLOSING',
              value: oc.ROTATION_MINUTES_BEFORE_CLOSING,
              fallback: sharedBefore,
              title: 'Window before site closes within which a shift counts as a prior close for rotation tracking. Leave empty to fall back to MINUTES_BEFORE_CLOSING.',
            },
          ],
        })}
        {subRule({
          title: 'Close-then-open prevention',
          sub: 'Penalise opening shift the day after a closing shift (wraps Sun → Mon).',
          activeKey: 'CLOSE_THEN_OPEN_ACTIVE',
          active: ctoActive,
          weightKey: 'CLOSE_THEN_OPEN_PENALTY',
          weight: ctoPenalty,
          weightLabel: 'Penalty when same employee closes day N and opens day N+1',
          windows: [
            {
              label: 'before close (min)',
              key: 'CLOSE_THEN_OPEN_MINUTES_BEFORE_CLOSING',
              value: oc.CLOSE_THEN_OPEN_MINUTES_BEFORE_CLOSING,
              fallback: sharedBefore,
              title: '"Did they close yesterday" window: how close to site close their previous shift must end to count as a late close. Leave empty to fall back to MINUTES_BEFORE_CLOSING.',
            },
          ],
        })}
      </div>
    );
  };

  // ── Section open/close (SECTION_OPEN_CLOSE_RULE) ───────────────
  // Per-operational-area opener/closer gate, sibling of the site-wide key
  // holder rule above. Off by default; area list is fixed to prevent key typos.
  const renderSectionOpenClose = () => {
    const sc = config.SECTION_OPEN_CLOSE_RULE || {};
    const active = !!sc.ACTIVE;
    const disabled = !active;
    const sections = sc.SECTIONS || {};
    const sharedAfter = sc.MINUTES_AFTER_OPENING ?? 30;
    const sharedBefore = sc.MINUTES_BEFORE_CLOSING ?? 30;
    const constraints = config.CONSTRAINTS || {};
    const shortagePenalty = constraints.SECTION_KEY_HOLDER_SHORTAGE_PENALTY ?? 4000;
    const priorityWeight = constraints.SECTION_KEY_HOLDER_PRIORITY_WEIGHT ?? 5;

    return (
      <section className="cfg-section">
        <header className="cfg-section-head">
          <span className="cfg-section-kicker">03</span>
          <h3 className="cfg-section-title">Section open/close</h3>
          <span className="cfg-section-sub">require an opener/closer present per operational area</span>
          <div className="cfg-role-switch" style={{ marginLeft: 'auto' }}>
            <Switch
              checked={active}
              onCheckedChange={(c) => handleChange('SECTION_OPEN_CLOSE_RULE.ACTIVE', c)}
            />
            <span>{active ? 'section rule on' : 'section rule off'}</span>
          </div>
        </header>

        <div className="cfg-rule-fields" style={{ marginBottom: 12 }}>
          <label className="cfg-field" title="Window after a section's own open within which a starting shift counts as opening that section.">
            <span className="cfg-field-label">after open (min)</span>
            <Input
              type="number"
              min="0"
              step="1"
              className="cfg-num-input"
              disabled={disabled}
              placeholder="30"
              value={sc.MINUTES_AFTER_OPENING ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                handleChange('SECTION_OPEN_CLOSE_RULE.MINUTES_AFTER_OPENING', v === '' ? 30 : parseInt(v, 10));
              }}
            />
          </label>
          <label className="cfg-field" title="Window before a section's own close within which an ending shift counts as closing that section.">
            <span className="cfg-field-label">before close (min)</span>
            <Input
              type="number"
              min="0"
              step="1"
              className="cfg-num-input"
              disabled={disabled}
              placeholder="30"
              value={sc.MINUTES_BEFORE_CLOSING ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                handleChange('SECTION_OPEN_CLOSE_RULE.MINUTES_BEFORE_CLOSING', v === '' ? 30 : parseInt(v, 10));
              }}
            />
          </label>
          <label className="cfg-field" title="Objective penalty when a required section has no available opener/closer (mirrors KEY_HOLDER_SHORTAGE_PENALTY).">
            <span className="cfg-field-label">shortage penalty</span>
            <Input
              type="number"
              min="0"
              step="any"
              className="cfg-num-input"
              disabled={disabled}
              value={shortagePenalty}
              onChange={(e) => {
                const v = e.target.value;
                handleChange('CONSTRAINTS.SECTION_KEY_HOLDER_SHORTAGE_PENALTY', v === '' ? 4000 : parseFloat(v));
              }}
            />
          </label>
          <label className="cfg-field" title="Weight rewarding higher-priority section openers/closers (mirrors KEY_HOLDER_PRIORITY_WEIGHT).">
            <span className="cfg-field-label">priority weight</span>
            <Input
              type="number"
              min="0"
              step="any"
              className="cfg-num-input"
              disabled={disabled}
              value={priorityWeight}
              onChange={(e) => {
                const v = e.target.value;
                handleChange('CONSTRAINTS.SECTION_KEY_HOLDER_PRIORITY_WEIGHT', v === '' ? 5 : parseFloat(v));
              }}
            />
          </label>
        </div>

        <div className="cfg-rules-grid">
          {SECTION_AREAS.map((area) => {
            const sec = sections[area] || {};
            const reqOpener = !!sec.REQUIRES_OPENER;
            const reqCloser = !!sec.REQUIRES_CLOSER;
            const rowActive = reqOpener || reqCloser;
            const windowDisabled = disabled || !rowActive;
            return (
              <article key={area} className={cn('cfg-rule', (!active || !rowActive) && 'is-inactive')}>
                <header className="cfg-rule-head">
                  <div className="cfg-rule-title-block">
                    <h4 className="cfg-rule-title">{humanize(area)}</h4>
                  </div>
                </header>
                <div className="cfg-rule-body">
                  <div className="cfg-rule-toggles">
                    {renderToggleField(
                      'Requires opener',
                      reqOpener,
                      (c) => handleChange(`SECTION_OPEN_CLOSE_RULE.SECTIONS.${area}.REQUIRES_OPENER`, c),
                      disabled
                    )}
                    {renderToggleField(
                      'Requires closer',
                      reqCloser,
                      (c) => handleChange(`SECTION_OPEN_CLOSE_RULE.SECTIONS.${area}.REQUIRES_CLOSER`, c),
                      disabled
                    )}
                  </div>
                  <div className="cfg-rule-fields" style={{ marginTop: 8 }}>
                    <label className="cfg-field" title="Per-area override; leave empty to use the shared window above.">
                      <span className="cfg-field-label">after open (min)</span>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        className="cfg-num-input"
                        disabled={windowDisabled}
                        placeholder={`${sharedAfter}`}
                        value={sec.MINUTES_AFTER_OPENING ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          handleChange(
                            `SECTION_OPEN_CLOSE_RULE.SECTIONS.${area}.MINUTES_AFTER_OPENING`,
                            v === '' ? null : parseInt(v, 10)
                          );
                        }}
                      />
                    </label>
                    <label className="cfg-field" title="Per-area override; leave empty to use the shared window above.">
                      <span className="cfg-field-label">before close (min)</span>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        className="cfg-num-input"
                        disabled={windowDisabled}
                        placeholder={`${sharedBefore}`}
                        value={sec.MINUTES_BEFORE_CLOSING ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          handleChange(
                            `SECTION_OPEN_CLOSE_RULE.SECTIONS.${area}.MINUTES_BEFORE_CLOSING`,
                            v === '' ? null : parseInt(v, 10)
                          );
                        }}
                      />
                    </label>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    );
  };

  // ── Role tabs ──────────────────────────────────────────────────
  const renderRoleTabs = () => (
    <div className="cfg-roletabs" role="tablist">
      {roles.map((role) => {
        const rc = config.ROLES[role];
        const active = role === currentRole;
        return (
          <button
            key={role}
            role="tab"
            aria-selected={active}
            onClick={() => setActiveRole(role)}
            className={cn('cfg-roletab', active && 'is-active', !rc.ACTIVE && 'is-inactive')}
          >
            <span className="cfg-roletab-dot" data-on={rc.ACTIVE} />
            <span className="cfg-roletab-name">{role}</span>
            <span className="cfg-roletab-meta">
              {Object.entries(rc).filter(([k, v]) => k !== 'ACTIVE' && typeof v === 'object' && v.ACTIVE).length}
              <span className="cfg-roletab-meta-slash">/</span>
              {Object.entries(rc).filter(([k, v]) => k !== 'ACTIVE' && typeof v === 'object').length}
            </span>
          </button>
        );
      })}
    </div>
  );

  // ── Time endpoint select ───────────────────────────────────────
  const renderTimeSelect = (value, onChange, disabled) => {
    const display = formatShiftValue(value);
    const options = SHIFT_TIME_OPTIONS.includes(display)
      ? SHIFT_TIME_OPTIONS
      : [display, ...SHIFT_TIME_OPTIONS];
    return (
      <Select
        value={display}
        onValueChange={(v) => onChange(parseShiftValue(v))}
        disabled={disabled}
      >
        <SelectTrigger className="cfg-time-trigger">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="cfg-time-content">
          {options.map((opt) => (
            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  // ── Ideal shifts chip editor (inline, no popover) ──────────────
  const renderIdealShifts = (shifts, onChange, disabled, compact = false) => (
    <div className={cn('cfg-shifts', compact && 'is-compact')}>
      {(!shifts || shifts.length === 0) && (
        <span className="cfg-shifts-empty">No ideal patterns — any shape accepted</span>
      )}
      {shifts && shifts.map((pair, idx) => (
        <div key={idx} className="cfg-shift-chip">
          {renderTimeSelect(pair[0], (v) => {
            const next = JSON.parse(JSON.stringify(shifts));
            next[idx][0] = v;
            onChange(next);
          }, disabled)}
          <span className="cfg-shift-arrow">→</span>
          {renderTimeSelect(pair[1], (v) => {
            const next = JSON.parse(JSON.stringify(shifts));
            next[idx][1] = v;
            onChange(next);
          }, disabled)}
          <button
            className="cfg-shift-remove"
            disabled={disabled}
            onClick={() => onChange(shifts.filter((_, i) => i !== idx))}
            aria-label="Remove pattern"
          >
            <X size={12} strokeWidth={2.5} />
          </button>
        </div>
      ))}
      <button
        className="cfg-shift-add"
        disabled={disabled}
        onClick={() => onChange([...(shifts || []), ['open', 'close']])}
      >
        <Plus size={12} strokeWidth={2.5} /> pattern
      </button>
    </div>
  );

  // ── Per-day IDEAL_SETTINGS editor ──────────────────────────────
  const IdealSettingsEditor = ({ settings, fallbackIdeal, onChange, disabled }) => {
    const [selected, setSelected] = useState(() => {
      const existing = DAY_KEYS.find((d) => settings && settings[d]);
      return existing || 'MONDAY';
    });

    const day = settings?.[selected];
    const overridden = !!day;

    const setDay = (next) => {
      onChange({ ...(settings || {}), [selected]: next });
    };

    const addDay = () => {
      setDay({ IDEAL: fallbackIdeal ?? 8, IDEAL_SHIFTS: [] });
    };

    const removeDay = () => {
      const next = { ...(settings || {}) };
      delete next[selected];
      onChange(next);
    };

    const copyToAll = () => {
      if (!day) return;
      const next = { ...(settings || {}) };
      DAY_KEYS.forEach((d) => {
        next[d] = JSON.parse(JSON.stringify(day));
      });
      onChange(next);
    };

    return (
      <div className="cfg-ideal">
        <div className="cfg-ideal-head">
          <span className="cfg-field-label">Per-day ideal shifts</span>
          <div className="cfg-ideal-actions">
            {overridden && (
              <>
                <button
                  className="cfg-mini-btn"
                  onClick={copyToAll}
                  disabled={disabled}
                  title="Apply these patterns to every day"
                >
                  <Copy size={11} /> copy to all
                </button>
                <button
                  className="cfg-mini-btn is-danger"
                  onClick={removeDay}
                  disabled={disabled}
                  title="Remove override — falls back to rule default"
                >
                  <RotateCcw size={11} /> reset day
                </button>
              </>
            )}
          </div>
        </div>

        <div className="cfg-day-pills" role="tablist">
          {DAY_KEYS.map((d) => {
            const hasOverride = settings && settings[d];
            const isSel = selected === d;
            return (
              <button
                key={d}
                role="tab"
                aria-selected={isSel}
                onClick={() => setSelected(d)}
                className={cn(
                  'cfg-day-pill',
                  isSel && 'is-selected',
                  hasOverride && 'is-overridden'
                )}
              >
                <span className="cfg-day-pill-label">{DAY_SHORT[d]}</span>
                {hasOverride && (
                  <span className="cfg-day-pill-badge">
                    {settings[d].IDEAL != null ? `${settings[d].IDEAL}h` : `${(settings[d].IDEAL_SHIFTS || []).length}`}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className={cn('cfg-day-editor', !overridden && 'is-empty')}>
          {overridden ? (
            <>
              <div className="cfg-day-row">
                <label className="cfg-field">
                  <span className="cfg-field-label">Ideal hours</span>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    className="cfg-num-input"
                    value={day.IDEAL ?? ''}
                    disabled={disabled}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDay({ ...day, IDEAL: v === '' ? null : parseFloat(v) });
                    }}
                  />
                </label>
              </div>
              <div className="cfg-day-row">
                <span className="cfg-field-label">Ideal patterns</span>
                {renderIdealShifts(
                  day.IDEAL_SHIFTS || [],
                  (v) => setDay({ ...day, IDEAL_SHIFTS: v }),
                  disabled
                )}
              </div>
            </>
          ) : (
            <div className="cfg-day-empty">
              <span>
                {DAY_SHORT[selected]} uses the rule default
                {fallbackIdeal != null && <> ({fallbackIdeal}h)</>}
              </span>
              <button className="cfg-mini-btn" onClick={addDay} disabled={disabled}>
                <Plus size={11} /> add {DAY_SHORT[selected]} override
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Substitutes chip list ──────────────────────────────────────
  const renderSubstitutes = (value, onChange, disabled) => {
    const opts = Object.keys(config.ROLES);
    return (
      <div className="cfg-shifts">
        {(value || []).map((sub, idx) => (
          <div key={idx} className="cfg-shift-chip">
            <Select
              value={sub}
              onValueChange={(v) => {
                const next = [...value];
                next[idx] = v;
                onChange(next);
              }}
              disabled={disabled}
            >
              <SelectTrigger className="cfg-sub-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="cfg-time-content">
                {opts.map((o) => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              className="cfg-shift-remove"
              disabled={disabled}
              onClick={() => onChange(value.filter((_, i) => i !== idx))}
            >
              <X size={12} strokeWidth={2.5} />
            </button>
          </div>
        ))}
        <button
          className="cfg-shift-add"
          disabled={disabled}
          onClick={() => onChange([...(value || []), opts[0]])}
        >
          <Plus size={12} strokeWidth={2.5} /> substitute
        </button>
      </div>
    );
  };

  // ── Breaks chip editor (retained, restyled) ────────────────────
  const renderBreaks = (arr, onChange, label, disabled) => (
    <div className="cfg-breaks">
      <span className="cfg-breaks-label">{label}</span>
      <div className="cfg-breaks-chips">
        {arr.map((rule, idx) => (
          <div key={idx} className="cfg-break-chip">
            <Input
              type="number"
              step="0.1"
              min="0"
              value={rule.shift_length_range[0]}
              disabled={disabled}
              onChange={(e) => {
                const n = [...arr];
                n[idx].shift_length_range[0] = parseFloat(e.target.value);
                onChange(n);
              }}
              className="cfg-break-num"
            />
            <span className="cfg-break-sep">–</span>
            <Input
              type="number"
              step="0.1"
              min="0"
              value={rule.shift_length_range[1]}
              disabled={disabled}
              onChange={(e) => {
                const n = [...arr];
                n[idx].shift_length_range[1] = parseFloat(e.target.value);
                onChange(n);
              }}
              className="cfg-break-num"
            />
            <span className="cfg-break-unit">h</span>
            <span className="cfg-break-arrow">→</span>
            <Input
              type="number"
              min="0"
              value={rule.break_length}
              disabled={disabled}
              onChange={(e) => {
                const n = [...arr];
                n[idx].break_length = parseInt(e.target.value, 10);
                onChange(n);
              }}
              className="cfg-break-num"
            />
            <span className="cfg-break-unit">min</span>
            <button
              className="cfg-shift-remove"
              disabled={disabled}
              onClick={() => onChange(arr.filter((_, i) => i !== idx))}
            >
              <X size={12} strokeWidth={2.5} />
            </button>
          </div>
        ))}
        <button
          className="cfg-shift-add"
          disabled={disabled}
          onClick={() => onChange([...arr, { shift_length_range: [0, 6], break_length: 0 }])}
        >
          <Plus size={12} strokeWidth={2.5} /> break
        </button>
      </div>
    </div>
  );

  // ── Atomic field renderers ─────────────────────────────────────
  const renderNumberField = (label, value, onChange, disabled, opts = {}) => (
    <label className="cfg-field">
      <span className="cfg-field-label">{label}</span>
      <Input
        type="number"
        step={opts.step || 'any'}
        min={opts.min}
        className="cfg-num-input"
        disabled={disabled}
        value={value === Infinity ? 'Infinity' : (value ?? '')}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === '' ? null : parseFloat(v));
        }}
      />
    </label>
  );

  const renderTextField = (label, value, onChange, disabled) => (
    <label className="cfg-field">
      <span className="cfg-field-label">{label}</span>
      <Input
        type="text"
        className="cfg-text-input"
        disabled={disabled}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );

  const renderToggleField = (label, value, onChange, disabled) => (
    <label className={cn('cfg-toggle', value && 'is-on')}>
      <Switch checked={!!value} onCheckedChange={onChange} disabled={disabled} />
      <span className="cfg-toggle-label">{label}</span>
    </label>
  );

  // ── Rule card ──────────────────────────────────────────────────
  const renderRuleCard = (ruleName, ruleConfig, rolePath, isRoleActive) => {
    const isActive = ruleConfig.ACTIVE && isRoleActive;
    const disabled = !isActive;

    const entries = Object.entries(ruleConfig).filter(
      ([k]) => !['ACTIVE', 'POINTS', 'IS_HARD'].includes(k)
    );

    const bounds = entries.filter(([k]) => PRIMARY_NUMERIC_KEYS.has(k));
    const timeStrings = entries.filter(([k]) => TIME_STRING_KEYS.has(k));
    const toggles = entries.filter(([, v]) => typeof v === 'boolean');
    const breaksEntry = entries.find(([k]) => k === 'BREAKS');
    const youthBreaksEntry = entries.find(([k]) => k === 'YOUTH_BREAKS');
    const idealShiftsEntry = entries.find(([k]) => k === 'IDEAL_SHIFTS');
    const idealSettingsEntry = entries.find(([k]) => k === 'IDEAL_SETTINGS');
    const substitutesEntry = entries.find(([k]) => k === 'SUBSTITUTES');
    const stretchEntry = entries.find(([k]) => k === 'STRETCH');
    const everythingElse = entries.filter(([k, v]) => {
      if (PRIMARY_NUMERIC_KEYS.has(k)) return false;
      if (TIME_STRING_KEYS.has(k)) return false;
      if (typeof v === 'boolean') return false;
      if (['BREAKS', 'YOUTH_BREAKS', 'IDEAL_SHIFTS', 'IDEAL_SETTINGS', 'SUBSTITUTES', 'STRETCH'].includes(k)) return false;
      return true;
    });

    return (
      <article key={ruleName} className={cn('cfg-rule', !isActive && 'is-inactive', ruleConfig.IS_HARD && 'is-hard')}>
        <header className="cfg-rule-head">
          <div className="cfg-rule-title-block">
            <Switch
              checked={ruleConfig.ACTIVE}
              onCheckedChange={(c) => handleChange(`${rolePath}.${ruleName}.ACTIVE`, c)}
              disabled={!isRoleActive}
            />
            <h4 className="cfg-rule-title">{humanize(ruleName)}</h4>
          </div>
          <div className="cfg-rule-meta">
            {ruleConfig.POINTS != null && (
              <label className="cfg-priority" title="Weight when scoring">
                <Zap size={11} strokeWidth={2.5} />
                <Input
                  type="number"
                  min="0"
                  className="cfg-priority-input"
                  value={ruleConfig.POINTS}
                  disabled={disabled}
                  onChange={(e) =>
                    handleChange(`${rolePath}.${ruleName}.POINTS`, parseInt(e.target.value, 10) || 0)
                  }
                />
              </label>
            )}
            {'IS_HARD' in ruleConfig && (
              <button
                className={cn('cfg-hard-btn', ruleConfig.IS_HARD && 'is-on')}
                disabled={disabled}
                onClick={() =>
                  handleChange(`${rolePath}.${ruleName}.IS_HARD`, !ruleConfig.IS_HARD)
                }
                title="Hard constraint — must be satisfied"
              >
                {ruleConfig.IS_HARD ? 'HARD' : 'soft'}
              </button>
            )}
          </div>
        </header>

        <div className="cfg-rule-body">
          {(bounds.length > 0 || timeStrings.length > 0) && (
            <div className="cfg-rule-fields">
              {bounds.map(([k, v]) => (
                <React.Fragment key={k}>
                  {renderNumberField(
                    humanize(k),
                    v,
                    (nv) => handleChange(`${rolePath}.${ruleName}.${k}`, nv),
                    disabled
                  )}
                </React.Fragment>
              ))}
              {timeStrings.map(([k, v]) => (
                <React.Fragment key={k}>
                  {renderTextField(
                    humanize(k),
                    v,
                    (nv) => handleChange(`${rolePath}.${ruleName}.${k}`, nv),
                    disabled
                  )}
                </React.Fragment>
              ))}
              {everythingElse
                .filter(([, v]) => typeof v === 'number')
                .map(([k, v]) => (
                  <React.Fragment key={k}>
                    {renderNumberField(
                      humanize(k),
                      v,
                      (nv) => handleChange(`${rolePath}.${ruleName}.${k}`, nv),
                      disabled
                    )}
                  </React.Fragment>
                ))}
            </div>
          )}

          {stretchEntry && (
            <div className="cfg-rule-subsection">
              <div className="cfg-field-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Switch
                  checked={!!stretchEntry[1].ACTIVE}
                  onCheckedChange={(c) => handleChange(`${rolePath}.${ruleName}.STRETCH.ACTIVE`, c)}
                  disabled={disabled}
                />
                Competency under stretch
                <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>
                  prefer higher-skilled staff on stretched slots
                </span>
              </div>
              <div className="cfg-rule-fields">
                {[
                  ['BASELINE_COMPETENCY', 'Baseline competency', { min: '1', max: '5', step: '1' }],
                  ['FLOOR', 'Floor (items ÷ head)', { step: '0.1' }],
                  ['PENALTY_WEIGHT', 'Penalty weight', { min: '0', step: '1' }],
                ].map(([k, label, attrs]) => (
                  <label className="cfg-field" key={k}>
                    <span className="cfg-field-label">{label}</span>
                    <Input
                      type="number"
                      {...attrs}
                      value={stretchEntry[1][k] ?? ''}
                      disabled={disabled}
                      onChange={(e) =>
                        handleChange(
                          `${rolePath}.${ruleName}.STRETCH.${k}`,
                          e.target.value === '' ? null : Number(e.target.value)
                        )
                      }
                    />
                  </label>
                ))}
                <label className="cfg-field">
                  <span className="cfg-field-label">Ceiling (blank = none)</span>
                  <Input
                    type="number"
                    step="0.1"
                    value={stretchEntry[1].CEILING ?? ''}
                    disabled={disabled}
                    onChange={(e) =>
                      handleChange(
                        `${rolePath}.${ruleName}.STRETCH.CEILING`,
                        e.target.value === '' ? null : Number(e.target.value)
                      )
                    }
                  />
                </label>
              </div>
              <span className="cfg-field-label" style={{ marginTop: 8, display: 'block' }}>
                Escalation tiers — at/above the stretch percentile, require the level
              </span>
              {(stretchEntry[1].TIERS || []).map((tier, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>≥ pctile</span>
                  <Input
                    type="number" min="0" max="1" step="0.05"
                    style={{ width: 80 }}
                    value={tier[0]}
                    disabled={disabled}
                    onChange={(e) => {
                      const t = (stretchEntry[1].TIERS || []).map((x) => [...x]);
                      t[idx][0] = Number(e.target.value);
                      handleChange(`${rolePath}.${ruleName}.STRETCH.TIERS`, t);
                    }}
                  />
                  <span style={{ fontSize: 12, color: '#6b7280' }}>→ level</span>
                  <Input
                    type="number" min="1" max="5" step="1"
                    style={{ width: 70 }}
                    value={tier[1]}
                    disabled={disabled}
                    onChange={(e) => {
                      const t = (stretchEntry[1].TIERS || []).map((x) => [...x]);
                      t[idx][1] = Number(e.target.value);
                      handleChange(`${rolePath}.${ruleName}.STRETCH.TIERS`, t);
                    }}
                  />
                  <button
                    type="button"
                    className="cfg-hard-btn"
                    disabled={disabled}
                    onClick={() =>
                      handleChange(
                        `${rolePath}.${ruleName}.STRETCH.TIERS`,
                        (stretchEntry[1].TIERS || []).filter((_, i) => i !== idx)
                      )
                    }
                    title="Remove tier"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="cfg-hard-btn"
                disabled={disabled}
                style={{ marginTop: 6 }}
                onClick={() =>
                  handleChange(`${rolePath}.${ruleName}.STRETCH.TIERS`, [
                    ...(stretchEntry[1].TIERS || []),
                    [0.7, 4],
                  ])
                }
              >
                + tier
              </button>
            </div>
          )}

          {idealShiftsEntry && !idealSettingsEntry && (
            <div className="cfg-rule-subsection">
              <span className="cfg-field-label">Ideal shift patterns</span>
              {renderIdealShifts(
                idealShiftsEntry[1] || [],
                (v) => handleChange(`${rolePath}.${ruleName}.IDEAL_SHIFTS`, v),
                disabled
              )}
            </div>
          )}

          {idealSettingsEntry && (
            <div className="cfg-rule-subsection">
              <IdealSettingsEditor
                settings={idealSettingsEntry[1]}
                fallbackIdeal={ruleConfig.IDEAL}
                onChange={(v) => handleChange(`${rolePath}.${ruleName}.IDEAL_SETTINGS`, v)}
                disabled={disabled}
              />
            </div>
          )}

          {breaksEntry && (
            <div className="cfg-rule-subsection">
              {renderBreaks(
                breaksEntry[1],
                (v) => handleChange(`${rolePath}.${ruleName}.BREAKS`, v),
                'Standard breaks',
                disabled
              )}
            </div>
          )}

          {youthBreaksEntry && (
            <div className="cfg-rule-subsection">
              {renderBreaks(
                youthBreaksEntry[1],
                (v) => handleChange(`${rolePath}.${ruleName}.YOUTH_BREAKS`, v),
                'Youth breaks',
                disabled
              )}
            </div>
          )}

          {substitutesEntry && (
            <div className="cfg-rule-subsection">
              <span className="cfg-field-label">Substitute roles</span>
              {renderSubstitutes(
                substitutesEntry[1] || [],
                (v) => handleChange(`${rolePath}.${ruleName}.SUBSTITUTES`, v),
                disabled
              )}
            </div>
          )}

          {toggles.length > 0 && (
            <div className="cfg-rule-toggles">
              {toggles.map(([k, v]) => (
                <React.Fragment key={k}>
                  {renderToggleField(
                    humanize(k),
                    v,
                    (nv) => handleChange(`${rolePath}.${ruleName}.${k}`, nv),
                    disabled
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </article>
    );
  };

  // ── Role panel ─────────────────────────────────────────────────
  const renderRolePanel = () => {
    if (!currentRole) return null;
    const roleConfig = config.ROLES[currentRole];
    const rules = Object.entries(roleConfig).filter(
      ([k, v]) => k !== 'ACTIVE' && typeof v === 'object' && v !== null
    );

    return (
      <section className="cfg-section">
        <header className="cfg-section-head">
          <span className="cfg-section-kicker">04</span>
          <h3 className="cfg-section-title">Rules</h3>
          <div className="cfg-role-switch">
            <Switch
              checked={roleConfig.ACTIVE}
              onCheckedChange={(c) => handleChange(`ROLES.${currentRole}.ACTIVE`, c)}
            />
            <span>
              {currentRole} {roleConfig.ACTIVE ? 'enabled' : 'disabled'}
            </span>
          </div>
        </header>
        <div className="cfg-rules-grid">
          {rules.map(([name, rc]) => renderRuleCard(name, rc, `ROLES.${currentRole}`, roleConfig.ACTIVE))}
        </div>
      </section>
    );
  };

  // ── Shell ──────────────────────────────────────────────────────
  return (
    <div className="collapsible-section cfg-root">
      <div className="collapsible-header" onClick={() => setIsOpen(!isOpen)}>
        <h2 className="collapsible-title cfg-root-title">
          <Settings className="settings-icon" />
          <span>
            {area ? `${area} ` : ''}
            {isLight ? 'Relaxed Configuration' : 'Default Configuration'}
          </span>
        </h2>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </div>

      {isOpen && (
        <div className="cfg-panel">
          {renderTopRow()}
          {renderConstraints()}
          {renderKeyHolders()}
          {renderSectionOpenClose()}
          <OpenShortShiftsSection config={config} handleChange={handleChange} kicker="04" />
          {roles.length > 0 && (
            <>
              {renderRoleTabs()}
              {renderRolePanel()}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ConfigPanel;
